/**
 * ScheduleSelector Component
 *
 * Provides granular control for scheduling with three inputs:
 * - Frequency: Weekly, Biweekly, Monthly
 * - Day: Adapts based on frequency (weekdays or day of month)
 * - Time: Time picker for scheduling
 */

import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Label,
} from '@strivelabs/ui-core';
import { MONTHLY_OPTIONS } from '@/utils/schedule';

interface ScheduleSelectorProps {
  schedule: string | null;
  day: string | null;
  time: string;
  scheduleOptions: { id: string; label: string }[];
  dayOptions: string[];
  onScheduleChange: (value: string) => void;
  onDayChange: (value: string) => void;
  onTimeChange: (value: string) => void;
  disabled?: boolean;
}

export const ScheduleSelector: React.FC<ScheduleSelectorProps> = ({
  schedule,
  day,
  time,
  scheduleOptions,
  dayOptions,
  onScheduleChange,
  onDayChange,
  onTimeChange,
  disabled = false,
}) => {
  const isMonthly = schedule === 'monthly';
  const isBiweekly = schedule === 'biweekly';
  const isDaily = schedule === 'daily';

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Frequency Selector */}
      <div className="w-full">
        <Label htmlFor="schedule" className="text-sm text-white/80">
          Frequency
        </Label>
        <Select
          value={schedule || ''}
          onValueChange={onScheduleChange}
          disabled={disabled}
        >
          <SelectTrigger
            id="schedule"
            className="mt-1 h-auto w-full justify-between rounded-md border-[0.5px] border-white/10 bg-white/[0.08] px-3 pb-[9px] pt-[8px] text-sm text-white backdrop-blur-sm hover:bg-white/10"
          >
            <SelectValue placeholder="Select frequency" />
          </SelectTrigger>
          <SelectContent>
            {scheduleOptions.map((option) => (
              <SelectItem
                className="pl-8 pr-2"
                key={option.id}
                value={option.id}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isBiweekly && (
          <p className="mt-1.5 text-xs text-white/60">
            Runs twice a month on selected day (1st-7th and 15th-21st)
          </p>
        )}
      </div>

      {!isDaily && (
        <div className="w-full">
          <Label htmlFor="day" className="text-sm text-white/80">
            {isMonthly ? 'Day of Month' : 'Day of Week'}
          </Label>
          <Select
            value={day || ''}
            onValueChange={onDayChange}
            disabled={disabled}
          >
            <SelectTrigger
              id="day"
              className="mt-1 h-auto w-full justify-between rounded-md border-[0.5px] border-white/10 bg-white/[0.08] px-3 pb-[9px] pt-[8px] text-sm text-white backdrop-blur-sm hover:bg-white/10"
            >
              <SelectValue placeholder={isMonthly ? "Select day of month" : "Select day of week"} />
            </SelectTrigger>
            <SelectContent>
              {isMonthly
                ? MONTHLY_OPTIONS.map((dayNum) => (
                    <SelectItem className="pl-8 pr-2" key={dayNum} value={dayNum}>
                      {dayNum}
                    </SelectItem>
                  ))
                : dayOptions.map((dayOption, index) => (
                    <SelectItem className="pl-8 pr-2" key={index} value={index.toString()}>
                      {dayOption}
                    </SelectItem>
                  ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Time Input */}
      <div className="w-full">
        <Label htmlFor="time" className="text-sm text-white/80">
          Time
        </Label>
        <Input
          id="time"
          type="time"
          value={time}
          onChange={(e) => onTimeChange(e.target.value)}
          disabled={disabled}
          className="mt-1 h-auto w-full rounded-md border-[0.5px] border-white/10 bg-white/[0.08] px-3 pb-[9px] pt-[8px] text-sm text-white placeholder:text-white/40 backdrop-blur-sm hover:bg-white/10 focus-visible:ring-white/20"
        />
      </div>
    </div>
  );
};
